/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useEffect } from 'react';

import { Colors } from 'src/common/ui/base/Colors';
import { Spacing } from 'src/common/ui/base/Spacing';
import { TypographyStyles } from 'src/common/ui/base/Typography';
import { BoulderIcon } from 'src/common/ui/components/BoulderIcon';
import { BoulderIconName } from 'src/common/ui/components/icons';

type TabItem = {
  active?: boolean;
  disabled?: boolean;
  icon?: BoulderIconName;
  key: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
};

export const Tab = ({ children, item }: React.PropsWithChildren<{ item: TabItem }>) => {
  const { active, disabled, icon, key, onClick } = item;
  return (
    <a
      css={css`
        cursor: ${disabled ? 'not-allowed' : 'pointer'};

        ${tabStyles}

        ${active ? activeTabStyles : null}
        ${disabled ? disabledTabStyles : null}
      `}
      href={`#tab-${key}`}
      data-tesid="nds-component-tab"
      onClick={onClick}
    >
      {icon && (
        <BoulderIcon
          css={css`
            display: inline-block;
            margin-right: ${Spacing.Unit2}px;
            vertical-align: middle;
            position: relative;
            top: -1px;
          `}
          icon={icon}
          size={18}
        />
      )}
      {children}
    </a>
  );
};

export const TabGroup = ({
  activeTab = null,
  tabs,
  onClick,
}: {
  activeTab: TabItem['key'] | null;
  tabs: Array<Omit<TabItem, 'active' | 'onClick'> & { label: string }>;
  onClick: (key: TabItem['key']) => void;
}) => {
  const [currentTab, setCurrentTab] = React.useState<string | null>(activeTab);

  const handleTabClick = (key: string) => {
    setCurrentTab(key);
    if (onClick) {
      onClick(key);
    }
  };

  useEffect(() => {
    setCurrentTab(activeTab);
  }, [activeTab]);

  return (
    <nav
      data-testid="nds-component-tab-group"
      css={css`
        border-bottom: 1px solid #e0e0e0;
        display: flex;
        gap: 0;
        margin: 0;
        overflow-x: auto;
        padding: 0;
        width: 100%;
      `}
    >
      {tabs.map(({ disabled, icon, key, label }) => (
        <Tab
          item={{
            active: currentTab === key,
            disabled,
            icon,
            key,
            onClick: e => {
              e.preventDefault();
              if (disabled) return;
              handleTabClick(key);
            },
          }}
          key={key}
        >
          {label}
        </Tab>
      ))}
    </nav>
  );
};

const borderWidth = 3;

const tabStyles = css`
  ${TypographyStyles.subtitle1};

  background-color: ${Colors.White};
  border-bottom: ${borderWidth}px solid ${Colors.White};
  color: ${Colors.CoolGray7};
  display: block;
  flex: 1;
  margin: 0;
  padding: ${Spacing.Unit3}px ${Spacing.Unit4}px;
  padding-bottom: ${Spacing.Unit3 - borderWidth}px;
  text-align: center;
  text-decoration: none;
  transition: 0.1s ease-in-out;
  white-space: nowrap;

  &:hover {
    background-color: ${Colors.CoolGray1};
    border-bottom-color: ${Colors.CoolGray1};
  }

  &:active,
  &:focus {
    background-color: ${Colors.CoolGray4};
    border-bottom-color: ${Colors.CoolGray4};
    outline: none;
  }
`;

const activeTabStyles = css`
  border-bottom-color: ${Colors.BlueSpruce};
  color: ${Colors.BlueSpruce};

  &:hover {
    border-bottom-color: ${Colors.BlueSpruce};
  }

  &:active,
  &:focus {
    border-bottom-color: ${Colors.BlueSpruce};
  }
`;

const disabledTabStyles = css`
  cursor: not-allowed;

  &,
  &:hover,
  &:active,
  &:focus {
    background-color: ${Colors.White};
    border-bottom-color: ${Colors.White};
    color: ${Colors.CoolGray4};
  }
`;
